import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  where,
  writeBatch
} from 'firebase/firestore';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  MessageSquareText,
  Plus,
  Send,
  X
} from 'lucide-react';

import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';

const CATEGORIES = [
  'Absence prévue',
  'Remplacement',
  'Demande administrative',
  'Document / attestation',
  'Problème pédagogique',
  'Demande événement',
  'Autre'
];

const STATUS_LABELS = {
  sent: 'Nouvelle',
  in_review: 'En cours',
  waiting_teacher: 'En attente de votre réponse',
  resolved: 'Traitée'
};

function statusLabel(value) {
  return STATUS_LABELS[value] || 'En cours';
}

function displayDate(value, withTime = false) {
  if (!value) return '—';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('fr-FR', withTime
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { dateStyle: 'short' }).format(date);
}

function requestTime(item) {
  return item?.lastMessageAt?.toMillis?.() || item?.updatedAt?.toMillis?.() || item?.createdAt?.toMillis?.() || 0;
}

export default function TeacherRequestsPage() {
  const { user, profile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [messages, setMessages] = useState([]);
  const [showComposer, setShowComposer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [form, setForm] = useState({ category: '', subject: '', concernedDate: '', message: '' });

  const teacherNotice = profile?.teacherNotice || {};
  const teacherName =
    [teacherNotice.firstName, teacherNotice.lastName].filter(Boolean).join(' ') ||
    profile?.fullname ||
    profile?.fullName ||
    profile?.displayName ||
    'Enseignant';

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    setError('');

    const source = query(collection(db, 'teacherRequests'), where('teacherId', '==', user.uid));
    return onSnapshot(source, (snapshot) => {
      const rows = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => requestTime(b) - requestTime(a));
      setRequests(rows);
      setLoading(false);
    }, (snapshotError) => {
      console.error('Demandes enseignant :', snapshotError);
      setError('Impossible de charger vos demandes pour le moment.');
      setLoading(false);
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return undefined;
    }

    return onSnapshot(collection(db, 'teacherRequests', selectedId, 'messages'), (snapshot) => {
      setMessages(
        snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0))
      );
    }, (snapshotError) => {
      console.error('Conversation enseignant :', snapshotError);
      setError('Impossible de charger cette conversation.');
    });
  }, [selectedId]);

  const selected = useMemo(
    () => requests.find((item) => item.id === selectedId) || null,
    [requests, selectedId]
  );

  async function createRequest(event) {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!form.category || !form.subject.trim() || !form.message.trim()) {
      setError('Choisissez une catégorie et renseignez l’objet ainsi que votre message.');
      return;
    }

    setSubmitting(true);
    try {
      const requestRef = doc(collection(db, 'teacherRequests'));
      const messageRef = doc(collection(db, 'teacherRequests', requestRef.id, 'messages'));
      const batch = writeBatch(db);
      const now = Timestamp.now();

      batch.set(requestRef, {
        teacherId: user.uid,
        teacherName,
        teacherUsername: profile?.username || '',
        category: form.category,
        subject: form.subject.trim(),
        concernedDate: form.concernedDate || '',
        status: 'sent',
        statusHistory: [{ status: 'sent', at: now, byType: 'teacher', byUid: user.uid }],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageBy: 'teacher'
      });

      batch.set(messageRef, {
        authorType: 'teacher',
        authorUid: user.uid,
        authorName: teacherName,
        text: form.message.trim(),
        createdAt: serverTimestamp()
      });

      await batch.commit();
      setForm({ category: '', subject: '', concernedDate: '', message: '' });
      setShowComposer(false);
      setSelectedId(requestRef.id);
      setNotice('Votre demande a été envoyée à l’administration.');
    } catch (submitError) {
      console.error('Création demande enseignant :', submitError);
      setError('Impossible d’envoyer votre demande. Réessayez dans quelques instants.');
    } finally {
      setSubmitting(false);
    }
  }

  async function sendReply(event) {
    event.preventDefault();
    if (!selected || !reply.trim()) return;
    setReplying(true);
    setError('');
    setNotice('');

    try {
      const requestRef = doc(db, 'teacherRequests', selected.id);
      const messageRef = doc(collection(db, 'teacherRequests', selected.id, 'messages'));
      const batch = writeBatch(db);
      const shouldResume = selected.status === 'waiting_teacher';
      const now = Timestamp.now();

      batch.set(messageRef, {
        authorType: 'teacher',
        authorUid: user.uid,
        authorName: teacherName,
        text: reply.trim(),
        createdAt: serverTimestamp()
      });

      const update = {
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageBy: 'teacher'
      };
      if (shouldResume) {
        update.status = 'in_review';
        update.statusHistory = [
          ...(Array.isArray(selected.statusHistory) ? selected.statusHistory : []),
          { status: 'in_review', at: now, byType: 'teacher', byUid: user.uid }
        ];
      }
      batch.update(requestRef, update);
      await batch.commit();
      setReply('');
      setNotice('Votre réponse a été envoyée.');
    } catch (replyError) {
      console.error('Réponse demande enseignant :', replyError);
      setError('Impossible d’envoyer votre réponse.');
    } finally {
      setReplying(false);
    }
  }

  if (selected) {
    return (
      <section className="teacher-request-page-v1">
        <button type="button" className="complaint-back-v3" onClick={() => { setSelectedId(''); setNotice(''); setError(''); }}>
          <ArrowLeft size={16} /> Retour aux demandes
        </button>

        <div className="parent-complaint-detail-head-v3">
          <div>
            <p className="eyebrow">DEMANDE À L’ADMINISTRATION</p>
            <h2>{selected.subject}</h2>
            <p>{selected.category}{selected.concernedDate ? ` · Date concernée : ${displayDate(selected.concernedDate)}` : ''}</p>
          </div>
          <span className={`complaint-status-v3 status-${selected.status}`}>{statusLabel(selected.status)}</span>
        </div>

        {notice && <div className="alert parent-notice-v0">{notice}<button type="button" onClick={() => setNotice('')}>×</button></div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="complaint-status-panel-v3 teacher-request-status-v1">
          <div className="complaint-status-panel-title-v3">
            <div><span>ÉTAT DE LA DEMANDE</span><strong>{statusLabel(selected.status)}</strong></div>
            <small>Dernière activité : {displayDate(selected.lastMessageAt || selected.updatedAt || selected.createdAt, true)}</small>
          </div>
        </div>

        <div className="complaint-thread-v3">
          <div className="complaint-thread-title-v3">
            <div><strong>Conversation</strong><span>Échanges avec l’administration</span></div>
            <small>{messages.length} message{messages.length > 1 ? 's' : ''}</small>
          </div>

          <div className="teacher-request-messages-v1">
            {messages.map((message) => (
              <article key={message.id} className={`complaint-message-v2 ${message.authorType === 'teacher' ? 'from-parent' : 'from-admin'}`}>
                <strong>{message.authorType === 'teacher' ? 'Vous' : message.authorName || 'Administration'}</strong>
                <p>{message.text}</p>
                <small>{displayDate(message.createdAt, true)}</small>
              </article>
            ))}
            {!messages.length && <div className="parent-empty">Aucun message dans cette conversation.</div>}
          </div>
        </div>

        {selected.status !== 'resolved' ? (
          <form className={`complaint-reply-composer-v3 ${selected.status === 'waiting_teacher' ? 'highlight' : ''}`} onSubmit={sendReply}>
            <label>{selected.status === 'waiting_teacher' ? 'L’administration attend votre réponse' : 'Ajouter un message'}</label>
            <textarea value={reply} onChange={(event) => setReply(event.target.value)} maxLength={5000} placeholder="Écrivez votre réponse…" required />
            <div>
              <span>Votre message sera ajouté à la conversation.</span>
              <button className="button button-primary" type="submit" disabled={replying || !reply.trim()}><Send size={16} />{replying ? 'Envoi…' : 'Envoyer'}</button>
            </div>
          </form>
        ) : (
          <div className="teacher-request-closed-v1"><CheckCircle2 size={18} /> Cette demande a été traitée par l’administration.</div>
        )}
      </section>
    );
  }

  return (
    <section className="teacher-request-page-v1">
      <div className="complaints-page-heading-v3">
        <div>
          <p className="eyebrow">COMMUNICATION</p>
          <h2>Demandes & communication</h2>
          <p>Contactez l’administration et suivez vos demandes dans une conversation dédiée.</p>
        </div>
        <button type="button" className="button button-primary complaints-new-button-v3" onClick={() => setShowComposer((current) => !current)}>
          {showComposer ? <X size={17} /> : <Plus size={17} />}
          {showComposer ? 'Fermer' : 'Nouvelle demande'}
        </button>
      </div>

      {notice && <div className="alert parent-notice-v0">{notice}<button type="button" onClick={() => setNotice('')}>×</button></div>}
      {error && <div className="alert alert-error">{error}</div>}

      {showComposer && (
        <article className="complaint-compose-card-v3 teacher-request-compose-v1">
          <form className="complaint-compose-form-v2" onSubmit={createRequest}>
            <div>
              <p className="eyebrow">NOUVELLE DEMANDE</p>
              <h3>Que souhaitez-vous demander ?</h3>
            </div>

            <label>Catégorie
              <select value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))} required>
                <option value="">Choisir une catégorie</option>
                {CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
            </label>

            <label>Objet
              <input value={form.subject} onChange={(event) => setForm((current) => ({ ...current, subject: event.target.value }))} maxLength={160} placeholder="Ex. Absence prévue le 18 septembre" required />
            </label>

            <label>Date concernée <span className="muted">(facultatif)</span>
              <input type="date" value={form.concernedDate} onChange={(event) => setForm((current) => ({ ...current, concernedDate: event.target.value }))} />
            </label>

            <label>Message
              <textarea value={form.message} onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))} maxLength={5000} placeholder="Expliquez votre demande à l’administration…" required />
            </label>

            <button className="button button-primary" type="submit" disabled={submitting}><Send size={16} />{submitting ? 'Envoi…' : 'Envoyer la demande'}</button>
          </form>
        </article>
      )}

      <article className="parent-card-v0 teacher-request-history-v1">
        <div className="parent-card-v0-heading">
          <div><p className="eyebrow">HISTORIQUE</p><h3>Mes demandes</h3></div>
          <MessageSquareText size={20} />
        </div>

        {loading ? <div className="parent-empty large">Chargement de vos demandes…</div> : requests.map((item) => (
          <button type="button" className={`teacher-request-row-v1 ${item.status === 'waiting_teacher' ? 'needs-reply' : ''}`} key={item.id} onClick={() => setSelectedId(item.id)}>
            <div className="teacher-request-row-icon-v1"><MessageSquareText size={18} /></div>
            <div className="teacher-request-row-copy-v1">
              <div><strong>{item.subject}</strong><span className={`complaint-status-v3 status-${item.status}`}>{statusLabel(item.status)}</span></div>
              <span>{item.category}{item.concernedDate ? ` · ${displayDate(item.concernedDate)}` : ''}</span>
              <small>{item.status === 'waiting_teacher' ? 'Votre réponse est attendue' : `Dernière activité : ${displayDate(item.lastMessageAt || item.updatedAt || item.createdAt, true)}`}</small>
            </div>
            <ChevronRight size={18} />
          </button>
        ))}

        {!loading && !requests.length && (
          <div className="complaint-empty-v3">
            <MessageSquareText size={28} />
            <strong>Aucune demande pour le moment</strong>
            <span>Utilisez « Nouvelle demande » pour contacter l’administration.</span>
          </div>
        )}
      </article>
    </section>
  );
}
